/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import React from 'react';
import * as Styles from '../../../../SharedModules/StyleConstant/StyleConstant';
import MaxLengthNumericEditor from '../../../../SharedModules/CustomAgGridEditor/inputComponent';
import {
  cellRenderer,
  textMatcherWith2Decimal,
} from '../../../../SharedModules/utilities/utilities';

// -ve value in red and bracket for dollars
const ragRenderer = (params) => {
  // const value = params.value;
  const value = parseFloat(params?.value);
  if (params?.data?.isBottomRow) {
    const pinnedValue = parseFloat(params?.value);
    if (pinnedValue < 0) {
      return (
        <b>
          <span style={{ color: '#eb0000' }}>
            (
            {`$ ${Math.abs(parseFloat(pinnedValue)).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
            )
          </span>
        </b>
      );
    }
    if (pinnedValue) {
      return (
        <b>
          {`$ ${parseFloat(pinnedValue)?.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
        </b>
      );
    }
    return '$0.00';
  }
  if (value < 0) {
    return (
      <span style={{ color: '#eb0000' }}>
        ($
        {Math.abs(parseFloat(value)).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
        )
      </span>
    );
  }
  if (value) {
    return `$ ${parseFloat(value)?.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return '$0.00';
};
// for units col for values

const fourDecimalRenderer = (params, a) => {
  const { value } = params;
  const { isTextColorRed } = params.colDef;
  const { printPrefixValue } = params.colDef;
  const { printPostfixValue } = params.colDef;
  if (value === '') return '';
  if (value < 0) {
    return (
      <span
        style={{
          color: isTextColorRed ? 'red' : '',
        }}
      >
        {`(${printPrefixValue ? `${printPrefixValue} ` : ''}${parseFloat(value)
          .toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          })
          .substring(1)})``${printPostfixValue || ''}`}
      </span>
    );
  }
  if (value > -1) {
    return (
      <span>
        {`${
          (printPrefixValue ? `${printPrefixValue} ` : '') +
          parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          })
        }${printPostfixValue || ''}`}
      </span>
    );
  }
  return params.value || '';
};
// upto 4 Decimal
const negativeRenderer = (params) => {
  if (params.value < 0) {
    return (
      <span style={{ color: '#eb0000' }}>
        (
        {Math.abs(params.value).toLocaleString('en-US', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        })}
        )
      </span>
    );
  }
  // console.log('params?.value', params?.value);
  if (params?.value) {
    return parseFloat(params.value)?.toLocaleString('en-US', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  }
  return '';
};
// where we can only select with number
const textMatcher = ({ filterOption, value: val, filterText }) => {
  const value = Math.abs(val).toString();
  const valueWithDollar = `$ ${value}`;
  if (filterText == null) {
    return false;
  }
  switch (filterOption) {
    case 'contains':
      return (
        value.indexOf(filterText) >= 0 ||
        valueWithDollar.indexOf(filterText) >= 0
      );
    case 'notContains':
      return value.indexOf(filterText.replace('$', '')) < 0;
    case 'equals':
      return value === filterText || valueWithDollar.indexOf(filterText) === 0;
    case 'notEqual':
      return value != filterText.replace('$', '');
    case 'startsWith':
      return (
        value.indexOf(filterText) === 0 ||
        valueWithDollar.indexOf(filterText) === 0
      );
    case 'endsWith':
      const index =
        value.lastIndexOf(filterText) || valueWithDollar.indexOf(filterText);
      return index >= 0 && index === value.length - filterText.length;
    default:
      return false;
  }
};

const comparator = (a, b) => {
  if (a === null && b === null) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return parseFloat(a) - parseFloat(b);
};

// grid on first Screen
export const manufacturersTableGrid = [
  {
    headerName: 'Manufacturer ID',
    field: 'mfgId',
    editable: false,
    sortable: true,
    sort: 'asc',
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_200,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Manufacturer Name',
    field: 'mfgName',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_350,
    cellRenderer: 'TextBoxRender',
    cellClass: 'frr-ellipsis-text',
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
];

export const mcoClaimsNDCTableGrid = [
  {
    headerName: 'NDC',
    field: 'firstColumn',
    editable: false,
    sortable: true,
    // sort: 'asc',
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_150,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },

  {
    headerName: 'Name',
    field: 'name',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_200,
    wrapText: true,
    cellRenderer: 'TextBoxRender',
    cellClass: 'frr-ellipsis-text',
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Group',
    field: 'groupHeader',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    wrapText: true,
    minWidth: Styles.Width_200,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Units',
    field: 'units',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_150,
    isTextColorRed: true,
    cellClass: 'frr-ellipsis-text',
    excelAlignment: 'right',
    cellRenderer: fourDecimalRenderer,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher,
      refreshValuesOnOpen: true,
    },
    comparator,
    alignment: 'right',
    isTextColorRed: true,
  },
  {
    headerName: 'URA',
    field: 'ura',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_120,
    isTextColorRed: true,
    cellRenderer: negativeRenderer,
    cellClass: 'frr-ellipsis-text',
    excelAlignment: 'right',
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher,
      refreshValuesOnOpen: true,
    },
    alignment: 'right',
    isTextColorRed: true,
  },
  {
    headerName: 'Invoiced Amt',
    field: 'invoicedAmount',
    editable: false,
    sortable: true,
    suppressMenu: false,
    isTextColorRed: true,
    printPrefixValue: '$',
    cellRenderer: ragRenderer,
    cellClass: 'frr-ellipsis-text',
    resizable: true,
    comparator,
    minWidth: Styles.Width_125,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher: textMatcherWith2Decimal,
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Percent',
    field: 'percent',
    editable: false,
    sortable: true,
    cellClass: 'frr-ellipsis-text',
    suppressMenu: false,
    resizable: true,
    isTextColorRed: true,
    printPostfixValue: '%',
    comparator,
    cellRenderer,
    exportZeroesTwo: true,
    minWidth: Styles.Width_120,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher: textMatcherWith2Decimal,
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Paid Amt',
    field: 'paidAmount',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_100,
    cellEditor: MaxLengthNumericEditor,
    isTextColorRed: true,
    printPrefixValue: '$',
    cellRenderer: ragRenderer,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    comparator,
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher: textMatcherWith2Decimal,
      refreshValuesOnOpen: true,
    },
  },
];

export const mcoClaimsManufacturerTableGrid = [
  {
    headerName: 'Manufacturer',
    field: 'firstColumn',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_171,
    cellEditor: MaxLengthNumericEditor,
    cellRenderer: 'TextBoxRender',
    cellClass: 'frr-ellipsis-text',
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },

  {
    headerName: 'Name',
    field: 'name',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_220,
    cellRenderer: 'TextBoxRender',
    cellClass: 'frr-ellipsis-text',
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Group',
    field: 'groupHeader',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    cellRenderer: 'TextBoxRender',
    cellClass: 'frr-ellipsis-text',
    minWidth: Styles.Width_220,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },

  {
    headerName: 'Invoiced Amt',
    field: 'invoicedAmount',
    editable: false,
    sortable: true,
    suppressMenu: false,
    isTextColorRed: true,
    printPrefixValue: '$',
    exportZeroesTwo: true,
    cellRenderer: ragRenderer,
    comparator,
    resizable: true,
    minWidth: Styles.Width_150,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher: textMatcherWith2Decimal,
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Percent',
    field: 'percent',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_120,
    isTextColorRed: true,
    printPostfixValue: '%',
    exportZeroesTwo: true,
    cellRenderer,
    comparator,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher: textMatcherWith2Decimal,
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Paid Amt',
    field: 'paidAmount',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    minWidth: Styles.Width_110,
    isTextColorRed: true,
    printPrefixValue: '$',
    cellRenderer: ragRenderer,
    comparator,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher: textMatcherWith2Decimal,
      refreshValuesOnOpen: true,
    },
  },
];
export const mcoClaimsTotalTableGrid = [
  {
    headerName: 'Name',
    field: 'name',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    cellRenderer: 'TextBoxRender',
    cellClass: 'frr-ellipsis-text',
    minWidth: Styles.Width_200,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Group',
    field: 'groupHeader',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    cellRenderer: 'TextBoxRender',
    cellClass: 'frr-ellipsis-text',
    minWidth: Styles.Width_200,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      refreshValuesOnOpen: true,
    },
  },

  {
    headerName: 'Invoiced Amt',
    field: 'invoicedAmount',
    editable: false,
    sortable: true,
    suppressMenu: false,
    isTextColorRed: true,
    printPrefixValue: '$',
    cellRenderer: ragRenderer,
    comparator,
    resizable: true,
    minWidth: Styles.Width_171,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher: textMatcherWith2Decimal,
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Percent',
    field: 'percent',
    editable: false,
    sortable: true,
    suppressMenu: false,
    resizable: true,
    isTextColorRed: true,
    printPostfixValue: '%',
    cellRenderer,
    comparator,
    minWidth: Styles.Width_120,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher: textMatcherWith2Decimal,
      refreshValuesOnOpen: true,
    },
  },
  {
    headerName: 'Paid Amt',
    field: 'paidAmount',
    editable: false,
    sortable: true,
    suppressMenu: false,
    isTextColorRed: true,
    printPrefixValue: '$',
    cellRenderer: ragRenderer,
    comparator,
    resizable: true,
    minWidth: Styles.Width_120,
    cellEditor: MaxLengthNumericEditor,
    cellStyle: { borderRightColor: Styles.BorderColor, textAlign: 'right' },
    filter: 'agTextColumnFilter',
    filterParams: {
      defaultOption: 'startsWith',
      suppressAndOrCondition: true,
      buttons: ['reset'],
      textMatcher: textMatcherWith2Decimal,
      refreshValuesOnOpen: true,
    },
  },
];
